import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2 } from "lucide-react";
import { getPlanets, aiRemedySuggest } from "../lib/api";
import { toast } from "sonner";

export default function Planets() {
  const [planets, setPlanets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [symptoms, setSymptoms] = useState("");
  const [age, setAge] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPlanets().then((p) => {
      setPlanets(p);
      setSelected(p[0]);
      const hash = window.location.hash.slice(1);
      if (hash) {
        const match = p.find((x) => x.id === hash);
        if (match) setSelected(match);
      }
    });
  }, []);

  const askAI = async () => {
    if (!symptoms.trim()) {
      toast.error("Describe your symptoms first.");
      return;
    }
    setLoading(true);
    setSuggestion(null);
    try {
      const res = await aiRemedySuggest({
        symptoms,
        planet: selected?.name,
        age: age ? Number(age) : undefined,
      });
      setSuggestion(res);
    } catch {
      toast.error("AI service unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!selected) return <div className="section container-page">Loading...</div>;

  return (
    <div data-testid="page-planets">
      <section className="pt-16 pb-8">
        <div className="container-page">
          <div className="eyebrow mb-4">The Nine Grahas</div>
          <h1 className="text-5xl md:text-6xl leading-tight max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Planet & Remedy Explorer
          </h1>
          <p className="mt-6 text-lg text-[#475569] max-w-2xl">Select a graha to reveal its physiological domain, associated diseases, and Materia Medica correspondences.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-page grid md:grid-cols-12 gap-8">
          {/* Grid of planets */}
          <div className="md:col-span-5">
            <div className="grid grid-cols-3 gap-3">
              {planets.map((p) => (
                <button
                  key={p.id}
                  data-testid={`planet-btn-${p.id}`}
                  onClick={() => setSelected(p)}
                  className={`aspect-square rounded-2xl border p-4 flex flex-col justify-between transition-all text-left ${
                    selected.id === p.id
                      ? "border-[#D4AF37] bg-[#D4AF37]/5 shadow-lg"
                      : "border-slate-200 bg-white hover:border-[#0EA5E9]"
                  }`}
                >
                  <div className="text-3xl" style={{ color: p.color }}>{p.symbol}</div>
                  <div className="text-xs font-medium text-[#0F172A] leading-tight">{p.name.split(" ")[0]}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="md:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="glass-card p-10"
              >
                <div className="flex items-center gap-6 mb-8">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center text-5xl" style={{ backgroundColor: `${selected.color}20`, color: selected.color }}>
                    {selected.symbol}
                  </div>
                  <div>
                    <div className="text-xs tracking-[0.25em] uppercase text-[#0EA5E9]">{selected.day}</div>
                    <h2 className="text-4xl mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{selected.name}</h2>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="text-xs tracking-widest uppercase text-[#D4AF37] mb-2">Governs</div>
                    <p className="text-[#0F172A]">{selected.governs}</p>
                  </div>
                  <div>
                    <div className="text-xs tracking-widest uppercase text-[#D4AF37] mb-2">Common Afflictions</div>
                    <p className="text-[#0F172A]">{selected.diseases}</p>
                  </div>
                  <div>
                    <div className="text-xs tracking-widest uppercase text-[#D4AF37] mb-3">Corresponding Remedies</div>
                    <div className="flex flex-wrap gap-2">
                      {selected.remedies.map((r) => (
                        <span key={r} className="px-4 py-1.5 rounded-full bg-[#F0F9FF] text-[#0EA5E9] text-sm font-medium border border-[#0EA5E9]/20">{r}</span>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs tracking-widest uppercase text-[#D4AF37] mb-2">Gemstone</div>
                      <p className="text-[#0F172A] font-medium">{selected.gemstone}</p>
                    </div>
                    <div>
                      <div className="text-xs tracking-widest uppercase text-[#D4AF37] mb-2">Ruling Day</div>
                      <p className="text-[#0F172A] font-medium">{selected.day}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* AI REMEDY SUGGESTER */}
      <section className="section bg-[#0F172A] text-white">
        <div className="container-page">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <div className="text-xs tracking-[0.28em] uppercase text-[#D4AF37] mb-4">AI-Powered</div>
              <h2 className="text-4xl md:text-5xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Remedy suggester</h2>
              <p className="text-white/70 leading-relaxed">Describe your symptoms and (optionally) your afflicted planet. Our AI, trained on Dr. Misra's system, will indicate likely remedy candidates. This is educational — not a prescription.</p>
              <div className="mt-8 space-y-4">
                <div>
                  <label className="text-xs tracking-widest uppercase text-white/50 mb-2 block">Symptoms</label>
                  <textarea
                    value={symptoms}
                    onChange={(e) => setSymptoms(e.target.value)}
                    data-testid="remedy-symptoms-input"
                    rows={4}
                    placeholder="Describe your primary concerns, mental state, modalities..."
                    className="w-full rounded-2xl bg-white/5 backdrop-blur border border-white/10 px-5 py-4 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-widest uppercase text-white/50 mb-2 block">Planet</label>
                    <div className="w-full rounded-2xl bg-white/5 border border-white/10 px-5 py-3 text-sm text-white/80" data-testid="remedy-planet-display">
                      {selected.name}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs tracking-widest uppercase text-white/50 mb-2 block">Age (optional)</label>
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      data-testid="remedy-age-input"
                      className="w-full rounded-2xl bg-white/5 border border-white/10 px-5 py-3 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>
                <button
                  onClick={askAI}
                  disabled={loading}
                  data-testid="remedy-submit"
                  className="btn-gold w-full disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <><Sparkles size={18} className="mr-2" /> Suggest remedies</>}
                </button>
              </div>
            </div>

            <div className="min-h-[400px]" data-testid="remedy-result">
              {!suggestion && !loading && (
                <div className="glass-card p-10 bg-white/5 border-white/10 text-center h-full flex flex-col items-center justify-center text-white/60">
                  <Sparkles size={40} className="text-[#D4AF37] mb-4" />
                  <p>Your AI-guided remedy suggestions will appear here.</p>
                </div>
              )}
              {suggestion && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card bg-white p-10 text-[#0F172A]">
                  <div className="text-xs tracking-widest uppercase text-[#D4AF37]">Primary Remedy</div>
                  <div className="text-3xl mt-1 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{suggestion.primary_remedy}</div>

                  {suggestion.supporting_remedies?.length > 0 && (
                    <div className="mb-6">
                      <div className="text-xs tracking-widest uppercase text-[#0EA5E9] mb-2">Supporting</div>
                      <div className="flex flex-wrap gap-2">
                        {suggestion.supporting_remedies.map((r) => (
                          <span key={r} className="px-3 py-1 rounded-full bg-[#F0F9FF] text-[#0EA5E9] text-sm">{r}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {suggestion.planet_analysis && (
                    <div className="mb-4">
                      <div className="text-xs tracking-widest uppercase text-[#0EA5E9] mb-2">Planetary Reading</div>
                      <p className="text-sm text-[#475569]">{suggestion.planet_analysis}</p>
                    </div>
                  )}
                  {suggestion.reasoning && (
                    <div className="mb-4">
                      <div className="text-xs tracking-widest uppercase text-[#0EA5E9] mb-2">Reasoning</div>
                      <p className="text-sm text-[#475569]">{suggestion.reasoning}</p>
                    </div>
                  )}
                  <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-[#475569] italic">{suggestion.disclaimer || "Educational only. Consult Dr. Misra for prescription."}</div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
